import Filter from './Filter';
import { Equals, NotEquals, EqualsAll, GreaterThanEquals, LowerThanEquals } from 'Resources/ComparisonTypes';
import DatePreset from 'App/services/datePresetService';
import type RequestBuilder from 'Resources/RequestBuilder';

type DateValue = { start: Date | number | string | null; end: Date | string | null; preset: string };

export type AddedToCampaignValue = {
	Campaign: { value: number[] | null; comparisonType: string };
	Date: { value: DateValue };
};

type FilterState = { value: AddedToCampaignValue; inactive?: boolean };

type RawFilter = { cid?: number[] | null; cc?: string; d?: DateValue };

const WHENEVER: DateValue = { start: null, end: null, preset: 'whenever' };

const hasPeriod = (date: DateValue) =>
	!!date.preset && date.preset !== 'whenever' && (date.preset !== 'custom' || !!date.start || !!date.end);

export const isActive = (value: AddedToCampaignValue) => !!value.Campaign.value?.length || hasPeriod(value.Date.value);

class AddedToCampaignFilter extends Filter {
	type = 'raw';
	title = 'campaign.addedToCampaign';
	displayType = 'addedToCampaign';
	filterName = 'AddedToCampaign';
	entity = 'contact';
	showOnSegment = true;
	listConfig: unknown;

	constructor(opts: Record<string, unknown> = {}) {
		super('AddedToCampaign');
		Object.assign(this, opts);
	}

	getText(filter: FilterState) {
		return (filter.value.Campaign.value?.length ? 1 : 0) + (hasPeriod(filter.value.Date.value) ? 1 : 0);
	}

	isInactive(filter: FilterState) {
		return !isActive(filter.value);
	}

	toUrl(filter: FilterState): RawFilter {
		return {
			cid: filter.value.Campaign.value,
			cc: filter.value.Campaign.comparisonType,
			d: filter.value.Date.value
		};
	}

	fromUrl(rawFilter: RawFilter): FilterState {
		return {
			value: {
				Campaign: { value: rawFilter.cid ?? null, comparisonType: rawFilter.cc || Equals },
				Date: { value: rawFilter.d || WHENEVER }
			}
		};
	}

	generate(): FilterState {
		return {
			value: {
				Campaign: { value: null, comparisonType: Equals },
				Date: { value: WHENEVER }
			}
		};
	}

	build(
		filter: FilterState,
		requestBuilder: RequestBuilder,
		getField: (key: string) => { field: string },
		useTags: boolean
	) {
		if (this.isInactive(filter)) {
			return;
		}
		const campaigns = filter.value.Campaign.value ?? [];
		const comparisonType = filter.value.Campaign.comparisonType || Equals;
		const date = filter.value.Date.value;
		const period = hasPeriod(date);
		const dates = period ? DatePreset.getDatesFromValue(date, useTags) : {};

		// Without a period the flat campaign field answers, as it does for every contact indexed before
		// projectMemberships existed; the nested copy is only needed to keep a campaign with its own date.
		const addGroup = (ids: number[], not: boolean) => {
			const group = requestBuilder.groupBuilder();
			if (!period) {
				group.addFilter(getField('project.id'), Equals, ids);
			} else if (ids.length) {
				group.addFilter(getField('projectMembership.id'), Equals, ids);
			} else {
				group.addFilter(getField('projectMembership.id'), NotEquals, null);
			}
			if (dates.start) {
				group.addFilter(getField('projectMembership.addedDate'), GreaterThanEquals, dates.start);
			}
			if (dates.end) {
				group.addFilter(getField('projectMembership.addedDate'), LowerThanEquals, dates.end);
			}
			if (not) {
				group.isNotFilter();
			}
			group.done();
		};

		if (comparisonType === EqualsAll && campaigns.length) {
			campaigns.forEach(id => addGroup([id], false));
		} else {
			addGroup(campaigns, comparisonType === NotEquals);
		}
	}
}

export default AddedToCampaignFilter;
